<script setup>
import {computed, ref, watch} from 'vue'
import {useRouter} from 'vue-router'

import ItemStatusIcons from "@/common/ItemStatusIcons.vue"
import {usePageContentDataStore} from '@/stores/pageContentData'
import {usePageEditorStore} from '@/stores/pageEditor'
import {useTemplatesStore} from '@/stores/templates'
import {useItemDataStore} from '@/stores/itemData'

const props = defineProps({
    pageContentId: Number
})

const router = useRouter()
const pageContentDataStore = usePageContentDataStore()
const pageEditorStore = usePageEditorStore()
const templatesStore = useTemplatesStore()
const itemDataStore = useItemDataStore()

const contentId = computed(() => pageContentDataStore.items?.find(o => o.id === props.pageContentId)?.contentId)

const content = ref(undefined)

watch(
    contentId,
    async (newContentId) => {
        if (!newContentId) {
            content.value = undefined
            return
        }
        content.value = await itemDataStore.get({
            tableName: 'content',
            id: newContentId
        })
    },
    {immediate: true}
)

const contentTypeTitle = computed(() => {
    return templatesStore.contentTypes?.find(o => o.id === content.value?.contentType)?.title
})

const pageId = computed(() => pageEditorStore.pageId)
const activePageContentId = computed(() => pageEditorStore.activePageContentId)
const hoverPageContentId = computed(() => pageEditorStore.hoverPageContentId)
const isActive = computed(() => props.pageContentId === activePageContentId.value)

const contentItem = computed(() => {
    return pageEditorStore.contentItems.find(o => o.pageContentId === props.pageContentId)
})

const isSectionContent = computed(() => {
    return pageEditorStore.isSectionContent(props.pageContentId)
})

const classNames = computed(() => {
    return [
        props.pageContentId === activePageContentId.value ? 'active' :
            props.pageContentId === hoverPageContentId.value ? 'list-group-item-hover' : ''
    ]
        .filter(Boolean)
        .join(' ')
})

// Methods
const mouseoverContent = () => {
    pageEditorStore.hoverPageContentId = props.pageContentId
}

const mouseoutContent = () => {
    pageEditorStore.hoverPageContentId = 0
}

const selectContent = async () => {
    await pageEditorStore.goToContent({
        router: router,
        pageContentId: props.pageContentId
    })

    await pageEditorStore.scrollToContent({pageContentId: props.pageContentId})
}
</script>

<template>
    <a role="button" class="list-group-item list-group-item-action py-1 small d-inline-flex align-items-center gap-1"
       :class="classNames" @mouseover="mouseoverContent" @mouseout="mouseoutContent" @click="selectContent">
        <span class="flex-fill small" :class="{'ps-2': !isSectionContent, 'ps-3': isSectionContent}">{{
                contentTypeTitle
            }}</span>

        <ItemStatusIcons :table-name="'content'" :id="contentId" :is-active="isActive"/>
    </a>
</template>

<style scoped>
.list-group-item {
    border-color: transparent !important;
}

.list-group-item-hover {
    z-index: 1;
    color: var(--bs-list-group-action-hover-color);
    text-decoration: none;
    background-color: rgba(9, 110, 253, 0.2);
}
</style>